// Add company — three short steps (Company, Owner, Review), then the server
// builds it with the same step runner trial activation uses and this panel
// shows each step finishing. Approved mockup "Add Company Wizard" (2026-10-11).
//
// Only what a person knows is asked; the database server, accounts and
// passwords come from the server's own settings (CITS-Server
// services/quickCreateCompany.service.js). Edit company keeps the full form.
import { useEffect, useMemo, useRef, useState } from 'react'
import api from '../../api/axios'
import { getPlans, TIERS, TIER_LABEL, type Tier } from '../../api/features'
import { ArrowPathIcon, CheckIcon, ClipboardDocumentIcon, XMarkIcon } from '@heroicons/react/24/outline'

export interface CreatedCompany {
  id: string
  company_name: string
  company_code: string
  admin_url_slug: string
}

interface Props {
  open: boolean
  onClose: () => void
  /** Called once the company row exists (list refresh). */
  onCreated: (c: CreatedCompany) => void
  onLoginAs: (c: CreatedCompany) => void
  onShowQr: (c: CreatedCompany) => void
  /** Hand off to the company's Setup panel (run single steps again). */
  onOpenSetup?: (c: CreatedCompany) => void
}

interface Step { key: string; label: string; status: 'waiting' | 'running' | 'done' | 'failed'; error?: string | null; skipped?: boolean }
interface Activation { state: 'waiting' | 'building' | 'ready' | 'failed' | 'held' | 'declined'; steps: Step[]; error?: string | null }
interface Template { filename: string; isDefault: boolean }

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 30)
const PW_WORDS = ['Gravel', 'Truck', 'Loader', 'Grader', 'Trench', 'Stone', 'Paver', 'Culvert', 'Rebar', 'Plow', 'Bucket', 'Hauler']
export function generatePassword(): string {
  const r = new Uint32Array(4)
  crypto.getRandomValues(r)
  const w = (i: number) => PW_WORDS[r[i] % PW_WORDS.length]
  return `${w(0)}-${w(1)}-${w(2)}-${10 + (r[3] % 90)}`
}

const inputCls = 'w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
const labelCls = 'block text-xs font-semibold text-slate-500 mb-1.5'
const hintCls = 'text-xs text-slate-500 mt-1.5 leading-relaxed'

function Segment<T extends string>({ value, options, onChange }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={`flex-1 py-2 rounded-md text-sm font-semibold transition-colors ${value === o.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export default function AddCompanyPanel({ open, onClose, onCreated, onLoginAs, onShowQr, onOpenSetup }: Props) {
  const [step, setStep] = useState(1)
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [codeTouched, setCodeTouched] = useState(false)
  const [codeCheck, setCodeCheck] = useState<{ state: 'idle' | 'checking' | 'ok' | 'bad'; message?: string }>({ state: 'idle' })
  const [tier, setTier] = useState<Tier>('standard')
  const [status, setStatus] = useState<'active' | 'trial'>('active')
  const [ownerName, setOwnerName] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [ownerPhone, setOwnerPhone] = useState('')
  const [signIn, setSignIn] = useState<'link' | 'set'>('link')
  const [password, setPassword] = useState('')
  const [sendEmail, setSendEmail] = useState(true)
  const [payPeriod, setPayPeriod] = useState<'weekly' | 'biweekly'>('weekly')
  const [dbName, setDbName] = useState('')
  const [template, setTemplate] = useState('')
  const [templates, setTemplates] = useState<Template[]>([])
  const [planCounts, setPlanCounts] = useState<Record<string, number>>({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [created, setCreated] = useState<(CreatedCompany & { portalUrl: string; password: string | null }) | null>(null)
  const [activation, setActivation] = useState<Activation | null>(null)
  const [copied, setCopied] = useState(false)
  const shownPassword = useRef('')

  // Fresh form each time the panel opens.
  useEffect(() => {
    if (!open) return
    setStep(1); setName(''); setCode(''); setCodeTouched(false); setCodeCheck({ state: 'idle' })
    setTier('standard'); setStatus('active'); setOwnerName(''); setOwnerEmail(''); setOwnerPhone('')
    setSignIn('link'); setPassword(''); setSendEmail(true); setPayPeriod('weekly'); setDbName(''); setTemplate('')
    setSubmitting(false); setError(''); setCreated(null); setActivation(null); setCopied(false); shownPassword.current = ''
    getPlans()
      .then((p) => setPlanCounts(Object.fromEntries(TIERS.map((t) => [t, (p.plan?.[t] || []).length]))))
      .catch(() => setPlanCounts({}))
    api.get('/super-admin/schema-templates')
      .then(({ data }) => setTemplates(data.templates || []))
      .catch(() => setTemplates([]))
  }, [open])

  // Code follows the name until someone edits it.
  useEffect(() => {
    if (!codeTouched) setCode(slugify(name))
  }, [name, codeTouched])

  // Domain the admin portals live under (citstime.app in prod, cmapps.dev on
  // stage) — the server sends it with every code check.
  const [portalDomain, setPortalDomain] = useState('citstime.app')

  // Availability, checked as you type.
  useEffect(() => {
    if (!code) { setCodeCheck({ state: 'idle' }); return }
    setCodeCheck({ state: 'checking' })
    const t = setTimeout(() => {
      api.get('/super-admin/companies/code-available', { params: { code } })
        .then(({ data }) => {
          if (data.portalDomain) setPortalDomain(data.portalDomain)
          setCodeCheck(data.available
          ? { state: 'ok' }
          : { state: 'bad', message: `${data.message || 'Not available.'}${data.suggestion ? ` Try ${data.suggestion}.` : ''}` })
        })
        .catch(() => setCodeCheck({ state: 'idle' }))
    }, 350)
    return () => clearTimeout(t)
  }, [code])

  // Poll the build.
  useEffect(() => {
    if (!created) return
    let stop = false
    const tick = async () => {
      try {
        const { data } = await api.get(`/super-admin/companies/${created.id}/activation`)
        if (!stop) setActivation(data)
        if (!stop && (data.state === 'building' || data.state === 'waiting')) setTimeout(tick, 1500)
      } catch {
        if (!stop) setTimeout(tick, 3000)
      }
    }
    tick()
    return () => { stop = true }
  }, [created])

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail.trim())
  const step1Ok = name.trim().length > 0 && codeCheck.state === 'ok'
  const step2Ok = ownerName.trim().length > 0 && emailOk && (signIn === 'link' || password.length >= 12)
  const willEmail = signIn === 'link' || sendEmail
  const effectiveDb = dbName.trim() || `cma_${code.replace(/-/g, '_')}`
  const defaultTemplate = templates.find((t) => t.isDefault)?.filename

  const whatSteps = useMemo(() => [
    { key: 'record', label: 'Company record', detail: '' },
    { key: 'features', label: 'Plan and features', detail: `${TIER_LABEL[tier] ?? tier}${planCounts[tier] != null ? ` · ${planCounts[tier]} features on` : ''}` },
    { key: 'database', label: 'Build database', detail: `${effectiveDb} from ${template || 'the current template'}` },
    { key: 'login', label: "Owner's login", detail: signIn === 'set' ? `${ownerEmail.trim()} with the password you set` : `${ownerEmail.trim()} (set-password link)` },
    { key: 'email', label: 'Welcome email', detail: willEmail ? 'sent to the owner' : 'skipped' },
  ], [tier, planCounts, effectiveDb, template, signIn, ownerEmail, willEmail])

  const create = async () => {
    setSubmitting(true)
    setError('')
    try {
      const { data } = await api.post('/super-admin/companies/quick-create', {
        company_name: name.trim(),
        company_code: code,
        subscription_tier: tier,
        status,
        owner: { name: ownerName.trim(), email: ownerEmail.trim(), phone: ownerPhone.trim() || undefined },
        owner_password: signIn === 'set' ? password : undefined,
        send_welcome_email: signIn === 'set' ? sendEmail : true,
        pay_period: payPeriod,
        advanced: { database_name: dbName.trim() || undefined, template: template || undefined },
      })
      shownPassword.current = signIn === 'set' && data.password === 'set' ? password : ''
      const c = { id: data.companyId, company_name: name.trim(), company_code: code, admin_url_slug: code }
      setCreated({ ...c, portalUrl: data.portalUrl, password: data.password })
      onCreated(c)
    } catch (e: any) {
      setError(e.response?.data?.message || 'Could not create the company')
    } finally {
      setSubmitting(false)
    }
  }

  const retry = async () => {
    if (!created) return
    setError('')
    try {
      await api.post(`/super-admin/companies/${created.id}/activation/retry`)
      setActivation((a) => (a ? { ...a, state: 'building' } : a))
      setCreated({ ...created }) // restart polling
    } catch (e: any) {
      setError(e.response?.data?.message || 'Could not retry')
    }
  }

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard blocked; the field is selectable */ }
  }

  if (!open) return null
  const building = !!created
  const ready = activation?.state === 'ready'
  const failed = activation?.state === 'failed'
  const stepNames = ['Company', 'Owner', 'Review']

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-slate-900/45" onClick={() => (!building || ready || failed ? onClose() : undefined)} />
      <div role="dialog" aria-label="Add company" className="relative w-full max-w-xl h-full bg-white shadow-2xl flex flex-col">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          <h2 className="flex-1 text-lg font-bold text-slate-900">Add company</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg hover:bg-slate-100">
            <XMarkIcon className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="flex gap-2 px-5 py-3 border-b border-slate-200">
          {stepNames.map((label, i) => {
            const n = i + 1
            const done = building || step > n
            const on = !building && step === n
            return (
              <div key={label} className={`flex-1 flex items-center gap-2 text-xs font-bold ${done ? 'text-green-600' : on ? 'text-blue-600' : 'text-slate-400'}`}>
                <span className={`w-6 h-6 rounded-full grid place-items-center border-2 ${done ? 'bg-green-600 border-green-600 text-white' : on ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'}`}>
                  {done ? <CheckIcon className="w-3.5 h-3.5" /> : n}
                </span>
                {label}
              </div>
            )
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {!building && step === 1 && (
            <>
              <div>
                <label htmlFor="ac-name" className={labelCls}>Company name</label>
                <input id="ac-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
              </div>
              <div>
                <label htmlFor="ac-code" className={labelCls}>Company code</label>
                <input id="ac-code" className={`${inputCls} font-mono`} value={code} onChange={(e) => { setCodeTouched(true); setCode(slugify(e.target.value)) }} />
                {code && (
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-slate-500">
                    Admin portal <span className="font-mono text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded">{code}.{portalDomain}</span>
                    Crew app code <span className="font-mono text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded">{code.toUpperCase()}</span>
                  </div>
                )}
                <p className={hintCls}>
                  Made from the name; change it if you like. Letters and numbers only.{' '}
                  {codeCheck.state === 'checking' && <span className="text-slate-500">Checking…</span>}
                  {codeCheck.state === 'ok' && <span className="font-semibold text-green-600">Available.</span>}
                  {codeCheck.state === 'bad' && <span className="font-semibold text-red-600">{codeCheck.message}</span>}
                </p>
              </div>
              <div>
                <span className={labelCls}>Plan</span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TIERS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      aria-pressed={tier === t}
                      onClick={() => setTier(t)}
                      className={`text-left p-3 rounded-xl border-2 transition-colors ${tier === t ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                    >
                      <span className="block text-sm font-bold text-slate-900">{TIER_LABEL[t] ?? t}</span>
                      <span className="text-xs text-slate-500">{planCounts[t] != null ? `${planCounts[t]} features` : '—'}</span>
                    </button>
                  ))}
                </div>
                <p className={hintCls}>Features come from the Plans page. You can add or remove single features for this company later.</p>
              </div>
              <div>
                <span className={labelCls}>Start as</span>
                <Segment value={status} onChange={setStatus} options={[{ value: 'active', label: 'Active' }, { value: 'trial', label: 'Trial (90 days)' }]} />
              </div>
            </>
          )}

          {!building && step === 2 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="ac-oname" className={labelCls}>Owner's name</label>
                  <input id="ac-oname" className={inputCls} value={ownerName} onChange={(e) => setOwnerName(e.target.value)} autoFocus />
                </div>
                <div>
                  <label htmlFor="ac-ophone" className={labelCls}>Phone (optional)</label>
                  <input id="ac-ophone" className={inputCls} value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} />
                </div>
              </div>
              <div>
                <label htmlFor="ac-oemail" className={labelCls}>Owner's email</label>
                <input id="ac-oemail" type="email" className={inputCls} value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
                <p className={hintCls}>This person signs in to the admin portal first and can add everyone else.</p>
              </div>
              <div>
                <span className={labelCls}>Owner's sign-in</span>
                <Segment
                  value={signIn}
                  onChange={(v) => { setSignIn(v); if (v === 'set' && !password) setPassword(generatePassword()) }}
                  options={[{ value: 'link', label: 'Email a setup link' }, { value: 'set', label: 'Set a password now' }]}
                />
                <p className={hintCls}>
                  {signIn === 'link'
                    ? 'They choose their own password from the welcome email.'
                    : 'For testing, or when you set the company up for them. They can change it after signing in. If this email already has a CITS Time password, that one is kept.'}
                </p>
              </div>
              {signIn === 'set' ? (
                <>
                  <div>
                    <label htmlFor="ac-pw" className={labelCls}>Password</label>
                    <div className="flex gap-2">
                      <input id="ac-pw" className={`${inputCls} font-mono`} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                      <button type="button" onClick={() => setPassword(generatePassword())} className="px-3 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                        Generate
                      </button>
                      <button type="button" onClick={copyPassword} className="px-3 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
                        <ClipboardDocumentIcon className="w-4 h-4" />{copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <p className={`${hintCls} ${password && password.length < 12 ? 'text-red-600' : ''}`}>At least 12 characters. Shown once on the finished screen so you can hand it over.</p>
                  </div>
                  <label className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 accent-green-600" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
                    <span className="flex-1">
                      <span className="block text-sm font-semibold text-slate-900">Also send the welcome email</span>
                      <span className="text-xs text-slate-500">Off is handy for test companies, so nobody gets mail.</span>
                    </span>
                  </label>
                </>
              ) : (
                <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200">
                  <CheckIcon className="w-5 h-5 text-green-600" />
                  <span className="flex-1">
                    <span className="block text-sm font-semibold text-slate-900">The welcome email is sent</span>
                    <span className="text-xs text-slate-500">Sign-in link, the crew app code and app store links.</span>
                  </span>
                </div>
              )}
              <div>
                <span className={labelCls}>Pay period</span>
                <Segment value={payPeriod} onChange={setPayPeriod} options={[{ value: 'weekly', label: 'Weekly' }, { value: 'biweekly', label: 'Every 2 weeks' }]} />
                <p className={hintCls}>Weeks start Sunday. The company can change this in its own settings.</p>
              </div>
            </>
          )}

          {!building && step === 3 && (
            <>
              <div className="rounded-xl border border-slate-200 divide-y divide-slate-200 text-sm">
                {[
                  ['Company', name.trim()],
                  ['Code', `${code} · ${code}.${portalDomain}`],
                  ['Plan', `${TIER_LABEL[tier] ?? tier}${planCounts[tier] != null ? ` · ${planCounts[tier]} features` : ''}`],
                  ['Starts as', status === 'active' ? 'Active' : 'Trial, 90 days'],
                  ['Owner', `${ownerName.trim()} · ${ownerEmail.trim()}`],
                  ["Owner's sign-in", signIn === 'set' ? 'Password set now' : 'Setup link by email'],
                  ['Welcome email', willEmail ? 'Yes' : 'No'],
                  ['Pay period', payPeriod === 'weekly' ? 'Weekly' : 'Every 2 weeks'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 px-3 py-2.5">
                    <span className="text-slate-500">{k}</span>
                    <span className="font-semibold text-slate-900 text-right break-all">{v}</span>
                  </div>
                ))}
              </div>
              <div>
                <span className={labelCls}>What happens when you press Create</span>
                <ol className="space-y-2">
                  {whatSteps.map((s, i) => (
                    <li key={s.key} className="flex gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 grid place-items-center text-xs font-bold shrink-0">{i + 1}</span>
                      <span><span className="font-semibold text-slate-900">{s.label}</span>{s.detail && <span className="block text-xs text-slate-500">{s.detail}</span>}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <details className="rounded-xl border border-dashed border-slate-300 p-3">
                <summary className="cursor-pointer text-sm font-semibold text-slate-700">Advanced (database name, template)</summary>
                <div className="mt-3 space-y-3">
                  <div>
                    <label htmlFor="ac-db" className={labelCls}>Database name</label>
                    <input id="ac-db" className={`${inputCls} font-mono`} placeholder={`cma_${code.replace(/-/g, '_')}`} value={dbName} onChange={(e) => setDbName(e.target.value.toLowerCase())} />
                    <p className={hintCls}>The database server and its accounts come from the server's settings.</p>
                  </div>
                  <div>
                    <label htmlFor="ac-tpl" className={labelCls}>Schema template</label>
                    <select id="ac-tpl" className={inputCls} value={template} onChange={(e) => setTemplate(e.target.value)}>
                      <option value="">Current template{defaultTemplate ? ` (${defaultTemplate})` : ''}</option>
                      {templates.filter((t) => !t.isDefault).map((t) => <option key={t.filename} value={t.filename}>{t.filename}</option>)}
                    </select>
                  </div>
                </div>
              </details>
            </>
          )}

          {building && (
            <>
              <div>
                <span className={labelCls}>{ready ? 'All set' : failed ? 'Setup stopped' : `Setting up ${created?.company_name}…`}</span>
                <ol className="space-y-2.5">
                  {(activation?.steps || whatSteps.map((s) => ({ key: s.key, label: s.label, status: 'waiting' as const }))).map((s, i) => {
                    const detail = whatSteps.find((w) => w.key === s.key)?.detail
                    return (
                      <li key={s.key} className="flex gap-3 text-sm">
                        <span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-bold shrink-0 ${
                          s.status === 'done' ? 'bg-green-600 text-white' : s.status === 'running' ? 'bg-blue-600 text-white animate-pulse motion-reduce:animate-none' : s.status === 'failed' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {s.status === 'done' ? <CheckIcon className="w-3.5 h-3.5" /> : s.status === 'failed' ? '!' : i + 1}
                        </span>
                        <span className="min-w-0">
                          <span className="font-semibold text-slate-900">{s.label}</span>
                          {s.status === 'failed' && s.error
                            ? <span className="block text-xs text-red-600 break-words">{s.error}</span>
                            : detail && <span className="block text-xs text-slate-500">{s.key === 'email' && 'skipped' in s && s.skipped ? 'skipped' : detail}</span>}
                        </span>
                      </li>
                    )
                  })}
                </ol>
              </div>
              {!ready && !failed && <p className={hintCls}>You can close this panel; setup keeps going and the company row shows its progress.</p>}
              {failed && (
                <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-800 flex items-center gap-3">
                  <span className="flex-1">{activation?.error || 'A step failed.'} Fix the cause, then try again from that step.</span>
                  <button type="button" onClick={retry} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700">
                    <ArrowPathIcon className="w-4 h-4" />Try again
                  </button>
                  {onOpenSetup && created && (
                    <button type="button" onClick={() => onOpenSetup(created)} className="px-3 py-1.5 rounded-lg border border-red-300 bg-white text-sm font-semibold text-red-700 hover:bg-red-50">
                      Open setup
                    </button>
                  )}
                </div>
              )}
              {ready && created && (
                <div className="rounded-xl bg-green-50 border border-green-200 p-4 space-y-3">
                  <div className="text-base font-bold text-green-700">{created.company_name} is ready</div>
                  <div className="text-sm text-slate-700 space-y-1">
                    {shownPassword.current ? (
                      <div>Owner sign-in: {ownerEmail.trim()} / <span className="font-mono select-all">{shownPassword.current}</span> <span className="text-xs text-slate-500">(shown once)</span></div>
                    ) : created.password === 'kept_existing' ? (
                      <div>{ownerEmail.trim()} already had a CITS Time password, so it was kept.</div>
                    ) : (
                      <div>The owner has their welcome email.</div>
                    )}
                    <div>The crew app code is <span className="font-mono">{created.company_code.toUpperCase()}</span>.</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a href={created.portalUrl} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">Open admin portal</a>
                    <button type="button" onClick={() => onLoginAs(created)} className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">Log in as company</button>
                    <button type="button" onClick={() => onShowQr(created)} className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">Crew app QR code</button>
                    {onOpenSetup && <button type="button" onClick={() => onOpenSetup(created)} className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50">Setup</button>}
                  </div>
                </div>
              )}
            </>
          )}

          {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
          {building ? (
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              {ready ? 'Done' : 'Close'}
            </button>
          ) : (
            <>
              <button type="button" onClick={() => (step > 1 ? setStep(step - 1) : onClose())} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                {step > 1 ? 'Back' : 'Cancel'}
              </button>
              {step < 3 ? (
                <button
                  type="button"
                  disabled={step === 1 ? !step1Ok : !step2Ok}
                  onClick={() => setStep(step + 1)}
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  disabled={submitting}
                  onClick={create}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                >
                  {submitting ? 'Creating…' : 'Create company'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
